import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { supabase } from '../../../../lib/supabase';
import { formatDate } from '../../../../utils/dateUtils';

// Increased character limit to accommodate long URLs (database column is TEXT type)
const MINUTES_CHAR_LIMIT = 2000;

// Timeout for save operations (ms) - prevents infinite "Saving..." state
const SAVE_TIMEOUT_MS = 15000;

interface EditMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  meeting: any;
  preventCloseOnOutsideClick?: boolean;
}

export default function EditMeetingModal({ isOpen, onClose, onSuccess, meeting, preventCloseOnOutsideClick = true }: EditMeetingModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose, preventCloseOnOutsideClick);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    minutes: meeting?.meet_minutes || ''
  });

  // Track character count
  const minutesCharCount = formData.minutes.length;

  // Re-sync formData whenever the modal opens or the meeting prop changes.
  // This is critical for the MeetingsAdmin page where the modal stays mounted
  // between different edit clicks, and also resets any stuck isSubmitting state.
  useEffect(() => {
    if (isOpen && meeting) {
      setFormData({ minutes: meeting.meet_minutes || '' });
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, meeting?.meet_comm, meeting?.meet_date]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Step 1: Verify the auth session is still valid before attempting to save.
    // After tab-switching, the Supabase JWT can be in a mid-refresh state, causing
    // the first request to hang for 10-30 seconds. Detecting this early gives the
    // user a clear, actionable message instead of an infinite "Saving..." spinner.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Your session has expired. Please refresh the page and log in again.');
        setIsSubmitting(false);
        return;
      }
    } catch (sessionErr) {
      console.error('Session check failed:', sessionErr);
      setError('Could not verify your session. Please refresh the page and try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Step 2: Build the save operation as a proper async function so TypeScript
      // infers the return type from the awaited result rather than the Supabase
      // query builder chain (which has incomplete types for this table).
      // The async wrapper also allows Promise.race to work cleanly with a timeout.
      type SaveResult = { data: any[] | null; error: any };

      const doSave = async (): Promise<SaveResult> => {
        // Cast supabase to `any` at the chain root so TypeScript doesn't apply
        // the incomplete generated type for comm_meetings (where .update() is
        // typed as `never` due to a missing or partial type definition).
        const result = await (supabase as any)
          .from('comm_meetings')
          .update({ meet_minutes: formData.minutes || null })
          .match({ meet_comm: meeting.meet_comm, meet_date: meeting.meet_date })
          .select();
        return { data: result.data as any[] | null, error: result.error };
      };

      // Step 3: Race the save against a hard timeout so the button is never stuck
      // indefinitely if Supabase hangs (e.g. stale auth token after tab switching).
      // Promise<never> is safe in Promise.race — T | never = T.
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(
          'The save request timed out after 15 seconds. ' +
          'Please check your connection and try again. ' +
          'If the problem persists, refresh the page.'
        )), SAVE_TIMEOUT_MS)
      );

      const { data: updatedData, error: updateError } = await Promise.race([doSave(), timeoutPromise]);

      if (updateError) throw updateError;

      // Step 4: Confirm at least one row was actually updated.
      // Supabase does NOT throw an error when an update matches 0 rows — it silently
      // returns an empty array. Without this check, saves appeared to succeed but
      // nothing was written to the database.
      if (!updatedData || updatedData.length === 0) {
        throw new Error(
          'The meeting record could not be found in the database. ' +
          'Please refresh the page and try again. ' +
          'If the problem persists, the record may have been deleted.'
        );
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating meeting record:', err);
      setError(err instanceof Error ? err.message : 'Failed to update meeting record. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">Edit Meeting Record</h3>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <form id="edit-meeting-form" onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Save failed</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Basic Information - Read Only (Primary Key cannot be edited) */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Meeting Information</h4>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Community and Date cannot be edited as they are part of the record identifier.
                To change these, please delete this meeting and create a new one.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Community</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {meeting.comm_name || meeting.meet_comm}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Date</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {meeting.meet_date ? formatDate(meeting.meet_date) : 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Notes and Docs */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Notes and Docs</h4>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Notes and Docs
              </label>
              <textarea
                value={formData.minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, minutes: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                placeholder="Enter meeting notes and document links"
                rows={8}
                maxLength={MINUTES_CHAR_LIMIT}
              />
              <div className="flex justify-between">
                <div className="mt-1">
                  <p className="text-xs text-gray-500">
                    Enter detailed information about the meeting (optional, max {MINUTES_CHAR_LIMIT} characters)
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Add URL to the document repository as needed. URLs will be automatically converted to clickable links when displayed.
                  </p>
                </div>
                <p className={`mt-1 text-xs ${minutesCharCount >= MINUTES_CHAR_LIMIT * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
                  {minutesCharCount}/{MINUTES_CHAR_LIMIT}
                </p>
              </div>
            </div>
          </div>

          {/* Audit Information */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Record Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Original Community</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {meeting.meet_comm}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Original Date</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {meeting.meet_date ? formatDate(meeting.meet_date) : 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-meeting-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
